import { usePageMeta } from "@/lib/seo";
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";
import { ArrowLeft, ShoppingBag, Plus, Sparkles, Moon } from "lucide-react";
import SeriesCarousel from "@/components/SeriesCarousel";
import { typeLabel } from "@/lib/labels";
import Vol2WaitlistBadge from "@/components/Vol2WaitlistBadge";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preorderCount, setPreorderCount] = useState(0);
  usePageMeta(product ? `${product.title} | Just be you` : "Shop | Just be you", product?.description);
  const { addItem } = useCart();
  const { format } = useCurrency();

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      if (r.data.preorder) {
        api.get(`/products/${slug}/purchase-count`).then((c) => setPreorderCount(c.data.count)).catch(() => {});
      }
    }).catch(() => {});
  }, [slug]);

  const handleBuyNow = () => {
    addItem(product, 1);
    navigate("/cart");
  };

  const handleAddToCart = () => {
    addItem(product, 1);
    toast.success(`Added to your basket`);
  };

  if (!product) {
    return <div className="max-w-6xl mx-auto px-6 md:px-12 py-24 text-ink/50 font-serif italic" data-testid="product-loading">gathering…</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 relative" data-testid="product-detail-page">
      <div className="absolute -top-20 right-10 w-96 h-96 bg-lavender/50 organic-blob blur-3xl -z-0 pointer-events-none" />

      <button onClick={() => navigate("/shop")} className="text-ink/60 hover:text-lapis text-xs tracking-[0.2em] uppercase mb-12 inline-flex items-center gap-2 font-semibold" data-testid="back-to-shop-btn">
        <ArrowLeft size={14} /> Back to shop
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start relative">
        <div className="lg:col-span-6 relative">
          <div className="absolute -inset-4 bg-peach/30 organic-blob blur-2xl -z-0" />
          <div className="aspect-[4/5] overflow-hidden relative rounded-[2rem]">
            <img src={product.image} alt={product.title} className="w-full h-full object-cover" data-testid="product-image" />
          </div>
        </div>

        <div className="lg:col-span-6 lg:pt-8">
          <div className="flex items-center gap-3 mb-5">
            <div className="overline">{typeLabel(product.type)}</div>
            {product.preorder && (
              <div className="inline-flex items-center gap-1.5 bg-peach text-white text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full font-semibold" data-testid="product-preorder-badge">
                Pre-order
              </div>
            )}
            {product.exclusive && (
              <div className="inline-flex items-center gap-1.5 bg-lapis text-canvas text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full font-semibold" data-testid="product-exclusive-badge">
                <Sparkles size={10} /> Only here
              </div>
            )}
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-lapis font-light leading-tight" data-testid="product-title">{product.title}</h1>
          <div className="mt-6 font-serif text-3xl text-lapis" data-testid="product-price">{format(product.price, product.price_inr)}</div>

          {product.slug === "21-days-magnet-of-miracles-vol-1" && (
            <div className="mt-5">
              <Vol2WaitlistBadge />
            </div>
          )}

          <div className="divider-soft my-8" />
          <p className="text-ink/70 leading-relaxed whitespace-pre-line" data-testid="product-description">
            {product.long_description || product.description}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <button onClick={handleBuyNow} disabled={loading} className="btn-primary" data-testid="product-buy-btn">
              <ShoppingBag size={16} /> {loading ? "Opening…" : product.preorder ? "Pre-order now" : "Buy now"}
            </button>
            <button onClick={handleAddToCart} className="btn-soft" data-testid="product-add-cart-btn">
              <Plus size={16} /> Add to basket
            </button>
          </div>

          {product.price_inr >= 2500 && (
            <p className="mt-4 text-xs text-ink/60" data-testid="split-pay-note">
              Prefer to pay in 2–3 parts?{" "}
              <a href="https://wa.me/919707788827?text=Hi%20Mehjabin%2C%20I%27d%20love%20a%20split%20payment%20plan%20%F0%9F%8C%99" target="_blank" rel="noopener noreferrer" className="underline text-lapis font-semibold">WhatsApp me</a>{" "}
              — we'll arrange it, no pressure.
            </p>
          )}

          {product.preorder && preorderCount > 0 && (
            <p className="mt-5 text-sm text-peach-deep font-medium inline-flex items-center gap-2" data-testid="preorder-count">
              <Sparkles size={13} /> {preorderCount} {preorderCount === 1 ? "soul has" : "souls have"} already pre-ordered
            </p>
          )}

          {product.preorder && product.preorder_date && (() => {
            const daysLeft = Math.max(0, Math.ceil((new Date(product.preorder_date + "T00:00:00") - Date.now()) / 86400000));
            if (daysLeft <= 0) return null;
            return (
              <div className="mt-5 inline-flex items-center gap-2.5 bg-lapis/5 border border-lapis/15 rounded-full px-5 py-2.5" data-testid="preorder-countdown">
                <Moon size={14} className="text-lapis" />
                <span className="text-sm text-lapis">
                  Arriving in <span className="font-serif italic font-semibold">{daysLeft} {daysLeft === 1 ? "day" : "days"}</span>
                  <span className="text-ink/50"> · {new Date(product.preorder_date + "T00:00:00").toLocaleDateString("en-IN", { month: "long", day: "numeric" })}</span>
                </span>
              </div>
            );
          })()}

          <div className="mt-12 grid grid-cols-2 gap-6 text-xs text-ink/60">
            <div>
              <div className="overline mb-2">Delivery</div>
              <div>{product.preorder ? product.preorder_note || "Delivered soon" : product.type === "physical" ? "Worldwide, 7–14 days" : "Instant access"}</div>
            </div>
            <div>
              <div className="overline mb-2">Held by</div>
              <div className="font-serif italic text-ink text-base">Mehjabin</div>
            </div>
          </div>
        </div>
      </div>

      {product.series_slug && (
        <SeriesCarousel series_slug={product.series_slug} current_id={product.id} />
      )}
    </div>
  );
}
